import { MentionableText } from '~/app/types'
import ReactDOMServer from 'react-dom/server'
import { getPillElement } from '~/react/utils/CustomerUtils'

export const toTitleCase = (str: string) =>
    str.replaceAll(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase())

const s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .slice(1)

/**
 * Generates a GUID
 */
export const guid = (): string => `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`

// Use the browser's built-in functionality to quickly and safely escape the string
// From http://goo.gl/HWLKR5
export const escapeHtml = (str: string) => {
    const div = document.createElement('div')
    div.append(document.createTextNode(str))
    // noinspection InnerHTMLJS
    return div.innerHTML
}

// If you want to use a string literal in a regular expression, call this first to escape special characters
export const escapeForUseInRegex = (stringForRegex: string) =>
    stringForRegex.replaceAll(/[$()*+./?[\\\]^{|}-]/g, String.raw`\$&`)

export const escapeForDataAttribute = (stringForDataAttribute: string) =>
    stringForDataAttribute.replaceAll('&', '&amp;').replaceAll('"', '&quot;').replace(/</, '&lt;')

/**
 * Formats an external url by prepending https:// to it if needed
 */
export const formatExternalUrl = (url: string) => (/^http(s?):\/\/(.*)$/.test(url) ? url : 'https://' + url)

export const applyMentions = (
    textWithMentions: MentionableText,
    isEditMode: boolean,
    shouldEscape?: boolean,
    atSymbol?: string,
    isCustomerPill?: boolean,
    suffix: string = ''
) => {
    const at = atSymbol === undefined ? '@' : atSymbol

    let { text } = textWithMentions
    if (!text) return

    // Escape HTML to prevent XSS
    // NOTE: this doesnt work on CSD text with mentions for some reason.
    if (shouldEscape) {
        text = escapeHtml(text)
    }

    // {#} regex
    const placeholderMatch = new RegExp(String.raw`{\d+}`, 'g')
    let mentionCount = 0

    let formattedText = text.replaceAll(placeholderMatch, (match, index, fullString) => {
        // Since we can't do regex lookbehinds in javascript, we'll need to peek back at the previous index to look for an escape
        if (index > 0 && fullString.charAt(index - 1) == '\\') {
            return match
        }
        // Mentions without mentions
        if (textWithMentions.mentions.length <= mentionCount) {
            return match
        }
        const mentionData = textWithMentions.mentions[mentionCount]
        let convertedPlaceholder: string
        if (isCustomerPill) {
            const pillElement = getPillElement(
                mentionData.mention_lookup_field_value,
                Boolean(mentionData.is_executive_sponsor),
                Boolean(mentionData.is_commercial_lead),
                Boolean(mentionData.is_rd_lead),
                Boolean(mentionData.is_account_lead),
                mentionData.mention_text_name,
                mentionData.logo,
                true
            )
            convertedPlaceholder = ReactDOMServer.renderToString(pillElement)
        } else if (!isEditMode && 'mention_object_url' in mentionData && mentionData.mention_object_url) {
            convertedPlaceholder = `<a href='${escapeForDataAttribute(
                mentionData.mention_object_url
            )}'                     class='mentionLink' data-object-name='${escapeForDataAttribute(
                mentionData.mention_object_name
            )}'                     data-lookup-field-value='${escapeForDataAttribute(
                mentionData.mention_lookup_field_value
            )}'                     data-public-id='${escapeForDataAttribute(
                mentionData.mention_lookup_field_value
            )}'                     data-full-name='${escapeHtml(
                escapeForDataAttribute(mentionData.mention_text_name)
            )}'>${at + escapeHtml(mentionData.mention_text_name)}</a>`
        } else {
            if (isEditMode) {
                convertedPlaceholder = at + escapeHtml(mentionData.mention_text_name)
            } else {
                // If we're not in edit mode, then that means there's no url for the mention, so bold it to show that it's a mention
                convertedPlaceholder =
                    "<strong class='mentionLink'>" + at + escapeHtml(mentionData.mention_text_name) + '</strong>'
            }
        }
        mentionCount++
        return convertedPlaceholder
    })

    // Next pass we need to remove the \{ escapes
    // Looking for '\' requires a double double escape: '\\\\', meanwhile '\{' matches '{'
    const escapedBracket = new RegExp(String.raw`\\{`, 'g')
    formattedText = formattedText.replaceAll(escapedBracket, () => '{')

    if (isCustomerPill && mentionCount > 0) return formattedText + suffix
    return formattedText
}

export const removeTags = (str: string): string => {
    return str.replaceAll(/<[^>]*>?/gm, '')
}

export const decodeHTMLSymbols = (str: string): string => {
    return str.replaceAll('&lt;', '<').replaceAll('&gt;', '>').replaceAll('&nbsp;', ' ').replaceAll('&amp;', '&')
}

export const htmlToText = (str: string): string => {
    return decodeHTMLSymbols(removeTags(str))
}

export const pluralizeNoun = (noun: string, count: number, showCount: boolean) => {
    return showCount ? (count === 1 ? `1 ${noun}` : `${count} ${noun}s`) : count === 1 ? noun : `${noun}s`
}
